import React, { useCallback, useContext, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import BubbleChartCard from '../../../../shared/components/v3/Cards/BubbleChartCard/BubbleChartCard';
import EmptyCard from '../../../../shared/components/v3/Cards/EmptyCard/EmptyCard';
import RankingCard from '../../../../shared/components/v3/Cards/RankingCard/RankingCard';
import { CompanyContext } from '../../../../shared/context/company/companyContext';
import { MetricsContext } from '../../../../shared/context/metrics/metricsContext';
import { mapRankingCardDataFromChart } from '../../../../shared/handlers/dataMapper';
import { IRankingCardData } from '../../../../shared/interfaces/cards';
import { IMetricsBenefits } from '../../../../shared/interfaces/metrics';
import MetricsByCompany from '../../../../shared/services/api/metrics/metricsByCompany/metrics';
import { METRICS_RANKING_CARD, TRANSLATIONS_METRICS_NE } from '../../../../shared/translations/metrics';

const BenefitsMetrics = () => {
  const { company, locale } = useContext(CompanyContext);
  const { periodSelection } = useContext(MetricsContext);
  const [loadingBenefitsChart, setLoadingBenefitsChart] = useState<boolean>(false);
  const [benefitsChartError, setBenefitsChartError] = useState<boolean>(false);
  const [chartData, setChartData] = useState<IMetricsBenefits>();
  const [rankingData, setRankingData] = useState<IRankingCardData>({});
  const { t } = useTranslation();

  const getBenefitsData = useCallback(async () => {
    setLoadingBenefitsChart(true);
    setBenefitsChartError(false);
    try {
      const response = await MetricsByCompany.getBenefitsMetrics(
        company.id,
        periodSelection.period,
        periodSelection.month,
        locale
      );
      setChartData(response);
      setRankingData(mapRankingCardDataFromChart(response.topBenefits));
      const bubbleChartData = response.topBenefits?.map((benefit) => ({
        ...benefit,
        id: benefit.id
      }));
      setChartData((prevData) => ({ ...prevData, topBenefits: bubbleChartData }));
    } catch {
      setBenefitsChartError(true);
    } finally {
      setLoadingBenefitsChart(false);
    }
  }, [company, periodSelection]);

  useEffect(() => {
    getBenefitsData();
  }, [periodSelection, company, getBenefitsData]);

  return (
    <div className="metrics-content-section-benefits">
      <div className="first-component">
        <BubbleChartCard
          title={t(TRANSLATIONS_METRICS_NE.benefits.chartCard.title)}
          colorScheme="benefits"
          unitsName={t(TRANSLATIONS_METRICS_NE.benefits.chartCard.unitsName)}
          cardData={
            chartData?.usePercentage
              ? [{ value: `${chartData?.usePercentage}%`, label: t(TRANSLATIONS_METRICS_NE.benefits.chartCard.label) }]
              : []
          }
          chartData={chartData?.topBenefits}
          isPercentage={true}
          loading={loadingBenefitsChart}
          error={benefitsChartError}
          action={getBenefitsData}
        />
      </div>
      <div className="second-component">
        <RankingCard
          title={t(METRICS_RANKING_CARD.benefits.title)}
          data={rankingData}
          valueText={t(METRICS_RANKING_CARD.benefits.valueText)}
          error={benefitsChartError}
          loading={loadingBenefitsChart}
          hasTabBar={false}
          isPercentage={true}
          emptyRankingCard={
            <EmptyCard
              primaryText={t(METRICS_RANKING_CARD.emptyState.emptyStateTitle)}
              secondaryText={t(METRICS_RANKING_CARD.emptyState.emptyStateDescription)}
            />
          }
        />
      </div>
    </div>
  );
};

export default BenefitsMetrics;
